import moment from 'moment'

// Pure helpers for the two rate pages (spec: 0041_exchange_rates_dashboard, w-admin).
//
// The API groups each table's flat columns by currency. Every currency entry carries `fields`, a
// map from the slot shown here (ghsRate, lowest, highest, toUsd) to the server's own column name
// (`ghs_to_usd_lowest`). Edits are kept under those column names, so the payload needs no
// camel/snake conversion.

export const TABLES = {
  banking: {
    title: 'Banking rates',
    purpose:
      'Used for every duty estimate. Copy them from the bank. Lowest and highest give the estimate its range.',
    slots: ['ghsRate', 'lowest', 'highest', 'toUsd'],
    unset: code => `Not set: duty in ${code} can't be worked out`
  },
  car: {
    title: 'Car rates',
    purpose: 'Used to price every car in the listings, on the site and in the app.',
    slots: ['ghsRate', 'toUsd'],
    unset: code => `Not set: cars in ${code} can't be priced`
  }
}

export const SLOT_LABELS = {
  ghsRate: 'Rate',
  lowest: 'Lowest',
  highest: 'Highest',
  toUsd: 'To USD'
}

const toNumber = value => {
  if (value === null || value === undefined || value === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : NaN
}

export const formatRate = value => {
  const n = toNumber(value)
  if (!n) return '0'
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
}

// "1 USD = GH₵ 11.30" — the direction of a rate is never in doubt.
export const rateLabel = (code, value) =>
  toNumber(value) > 0 ? `1 ${code} = GH₵ ${formatRate(value)}` : 'Not set'

// A banking to-USD rate at 0 is worked out from the GHS rates; a car one at 0 is simply missing.
export const toUsdLabel = (table, code, value) => {
  if (toNumber(value) > 0) return `1 ${code} = $${formatRate(value)}`
  return table === 'banking' ? 'Worked out' : 'Not set'
}

// {column: number} for the current rates.
export const currentValues = rates => {
  const out = {}
  ;(rates?.currencies || []).forEach(currency => {
    Object.entries(currency.fields || {}).forEach(([slot, field]) => {
      out[field] = toNumber(currency[slot])
    })
  })
  return out
}

// Change in %, one decimal, or null when there was nothing to compare against.
export const percentChange = (old, next) => {
  const before = toNumber(old)
  const after = toNumber(next)
  if (!(before > 0) || Number.isNaN(after)) return null
  return Math.round(((after - before) / before) * 1000) / 10
}

// [{field, old, next}] for every edit that differs from the current value.
export const changedFields = (rates, edits) => {
  const before = currentValues(rates)
  return Object.entries(edits || {})
    .map(([field, value]) => ({ field, old: before[field] ?? 0, next: toNumber(value) }))
    .filter(change => Number.isNaN(change.next) || change.next !== change.old)
}

// What the page can check before sending: numbers, no negatives, bands in order (banking, and only
// for currencies being edited, as the API does). {column: message}.
export const localErrors = (table, rates, edits) => {
  const errors = {}
  const values = { ...currentValues(rates) }
  Object.entries(edits || {}).forEach(([field, value]) => {
    const n = toNumber(value)
    if (Number.isNaN(n)) errors[field] = 'Enter a number.'
    else if (n < 0) errors[field] = 'A rate can\'t be negative.'
    else values[field] = n
  })
  if (table !== 'banking') return errors
  ;(rates?.currencies || []).forEach(({ fields = {} }) => {
    const touched = [fields.ghsRate, fields.lowest, fields.highest].some(f => f && f in (edits || {}))
    if (!touched) return
    const central = values[fields.ghsRate]
    const low = values[fields.lowest]
    const high = values[fields.highest]
    if (low && central && low > central) errors[fields.lowest] = 'Lowest can\'t be above the rate.'
    if (high && central && high < central) errors[fields.highest] = 'Highest can\'t be below the rate.'
  })
  return errors
}

export const toPayload = changes => {
  const rates = {}
  changes.forEach(({ field, next }) => {
    rates[field] = String(next)
  })
  return rates
}

// The API's field errors ([{field, message}]) as {column: message}.
export const serverErrors = error => {
  const list = error?.response?.data?.errors
  if (!Array.isArray(list)) return {}
  return list.reduce((acc, { field, message }) => ({ ...acc, [field]: message }), {})
}

export const lastChanged = rates => {
  if (!rates?.id) return 'No rates saved yet'
  const when = moment(rates.updatedAt).format('D MMM YYYY, HH:mm')
  return rates.savedBy ? `Last changed ${when} by ${rates.savedBy}` : `Last changed ${when}`
}

const FIELD_WORDS = { ghsRate: 'rate', lowest: 'lowest', highest: 'highest', toUsd: 'to USD' }

// {column: "USD lowest"} for naming a rate outside its row (the confirmation dialog).
export const fieldLabels = rates => {
  const out = {}
  ;(rates?.currencies || []).forEach(currency => {
    Object.entries(currency.fields || {}).forEach(([slot, field]) => {
      out[field] = `${currency.code} ${FIELD_WORDS[slot]}`
    })
  })
  return out
}
